import type {ComponentProps, ComponentType, ForwardedRef, ReactNode, Ref} from "react";
import {forwardRef} from "react";
import type {
  DSFieldProps,
  DSInputProps,
  DSInputWithButtonProps,
  DSInputWithPostfixProps,
  DSSelectProps,
  DSTextAreaProps,
  DSTagSelectProps,
} from "@cdx/ds";
import {
  css,
  DSField,
  DSInput,
  DSInputWithButton,
  DSInputWithPostfix,
  DSTextArea,
  DSSelect,
  DSTagSelect,
  DSCheckbox,
  Col,
  Text,
} from "@cdx/ds";
import {fixedForwardRef} from "@cdx/ds/utils/type-utils";
import type {RadioGroupProps, CheckboxGroupProps} from "../forms/RadioGroup";
import {CheckboxGroup, RadioGroup} from "../forms/RadioGroup";
import type {NeoField, UseMetaArgs} from "./lib/create-neo-form";

export const shouldShowError = (meta: UseMetaArgs) => {
  if (meta.errors === null || meta.errors.length === 0) return false;
  return (meta.hasChanged && !meta.isFocussed) || meta.submitAttemptCount > 0;
};

type NeoFieldWrapperProps<TParsed = any> = {
  field: NeoField<any, TParsed>;
} & Omit<DSFieldProps, "fieldId" | "errors">;
export const NeoFieldWrapper = ({field, ...rest}: NeoFieldWrapperProps) => {
  const {useMeta, id, useCustomDataSlice} = field;
  const errors = useMeta((meta) =>
    shouldShowError(meta) ? meta.errors.map((e) => e.message).slice(0, 1) : []
  );
  const serverError = useCustomDataSlice("serverFieldError", (s) => s?.[field.name]);
  return (
    <DSField {...rest} fieldId={id} errors={serverError ? [...errors, serverError] : errors} />
  );
};

type MinusFieldProps<T extends Record<string, any>> = Omit<
  T,
  "onChange" | "id" | "value" | "ref" | "name"
>;

export type NeoFieldProps<TParsed, TProps extends Record<string, any>, TInput = string> = Pick<
  DSFieldProps,
  "label" | "hint"
> & {
  field: NeoField<TInput, TParsed>;
  wrapperClassName?: string;
} & MinusFieldProps<TProps>;

/**
 * Creates a standard field component from a DS component.
 * Wraps it with NeoFieldWrapper and wires up value, handlers, and error state.
 *
 * Options:
 * - `errorProp`: Name of the error prop to pass (default: `"hasError"`).
 *   Set to `false` to skip passing error state entirely.
 *   Use `"showingErrors"` for RadioGroup/CheckboxGroup-style components.
 *
 * For generic components or custom layouts, create fields manually instead.
 */
export const createField = <
  TRef extends Element,
  TComponentProps extends Record<string, any>,
  TInput = TComponentProps extends {value: infer V} ? V : string,
>(
  Component: ComponentType<TComponentProps>,
  opts?: {errorProp?: string | false}
) => {
  type Props = NeoFieldProps<any, TComponentProps, TInput> & {onChange?: (next: TInput) => void};
  const errorPropName = opts?.errorProp ?? "hasError";
  return fixedForwardRef(function Field(props: Props, ref: Ref<TRef>) {
    const {field, label, hint, wrapperClassName, ...rest} = props;
    const {useValue, useMeta, getHandlers, name, id} = field;
    const showErrors = useMeta((meta: UseMetaArgs) => shouldShowError(meta));
    return (
      <NeoFieldWrapper field={field} label={label} hint={hint} className={wrapperClassName}>
        <Component
          name={name}
          id={id}
          {...(rest as any)}
          {...getHandlers({...(rest as any), ref})}
          value={useValue()}
          {...(errorPropName ? {[errorPropName]: showErrors} : undefined)}
        />
      </NeoFieldWrapper>
    );
  });
};

export const TextField = createField<HTMLInputElement, DSInputProps>(DSInput);
export const TextAreaField = createField<HTMLInputElement, DSTextAreaProps>(DSTextArea);

type PlainInputFieldProps<TParsed = any> = NeoFieldProps<
  TParsed,
  Omit<ComponentProps<"input">, "onChange">
>;
export const PlainInputField = forwardRef<HTMLInputElement, PlainInputFieldProps>(
  ({field, label, hint, wrapperClassName, ...rest}, ref) => {
    const {useValue, getHandlers, name, id} = field;
    const {onChange, ...handlers} = getHandlers({...rest, ref: ref});
    return (
      <NeoFieldWrapper field={field} label={label} hint={hint} className={wrapperClassName}>
        <input
          name={name}
          id={id}
          {...rest}
          {...handlers}
          onChange={(e) => onChange(e.target.value)}
          value={useValue()}
        />
      </NeoFieldWrapper>
    );
  }
);

type SelectFieldProps<T> = NeoFieldProps<T, DSSelectProps<T>, T>;
const InnerSelectField = <const T,>(
  props: SelectFieldProps<T>,
  passedRef: ForwardedRef<HTMLButtonElement>
) => {
  const {field, label, hint, wrapperClassName, ...rest} = props;
  const {useValue, useMeta, getHandlers, name, id} = field;
  const showErrors = useMeta((meta) => shouldShowError(meta));
  return (
    <NeoFieldWrapper field={field} label={label} hint={hint} className={wrapperClassName}>
      <DSSelect
        name={name}
        id={id}
        {...rest}
        {...getHandlers({...rest, ref: passedRef})}
        value={useValue()}
        hasError={showErrors}
      />
    </NeoFieldWrapper>
  );
};

export const SelectField = fixedForwardRef(InnerSelectField);

export const TextFieldWithPostfix = createField<HTMLInputElement, DSInputWithPostfixProps>(
  DSInputWithPostfix
);
export const TextFieldWithButton = createField<HTMLInputElement, DSInputWithButtonProps>(
  DSInputWithButton
);

type MiniTextFieldProps<TParsed = any> = {
  field: NeoField<string, TParsed>;
} & MinusFieldProps<DSInputProps>;
export const MiniTextField = forwardRef<HTMLInputElement, MiniTextFieldProps>(
  ({field, ...rest}, ref) => {
    const {useValue, useMeta, getHandlers, name, id} = field;
    const showErrors = useMeta((meta) => shouldShowError(meta));
    return (
      <DSInput
        name={name}
        id={id}
        size="xs"
        {...rest}
        {...getHandlers({...rest, ref})}
        value={useValue()}
        hasError={showErrors}
        className={css({width: "32px", textAlign: "center"}, rest.className)}
      />
    );
  }
);

type RadioGroupFieldProps<T> = NeoFieldProps<T, RadioGroupProps<T>, T>;

const InnerRadioGroupField = <const T,>(
  props: RadioGroupFieldProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const {field, label, hint, wrapperClassName, ...rest} = props;
  const {useValue, useMeta, getHandlers, name} = field;
  const showErrors = useMeta((meta) => shouldShowError(meta));
  return (
    <NeoFieldWrapper field={field} label={label} hint={hint} className={wrapperClassName}>
      <RadioGroup
        {...rest}
        name={name}
        {...getHandlers({...rest, ref})}
        value={useValue()}
        showingErrors={showErrors}
      />
    </NeoFieldWrapper>
  );
};

export const RadioGroupField = fixedForwardRef(InnerRadioGroupField);

type CheckboxGroupFieldProps<T extends string> = NeoFieldProps<
  Record<T, boolean>,
  CheckboxGroupProps<T>,
  Record<T, boolean>
>;

const InnerCheckboxGroupField = <const T extends string>(
  props: CheckboxGroupFieldProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const {field, label, hint, wrapperClassName, ...rest} = props;
  const {useValue, useMeta, getHandlers, name} = field;
  const showErrors = useMeta((meta) => shouldShowError(meta));
  return (
    <NeoFieldWrapper field={field} label={label} hint={hint} className={wrapperClassName}>
      <CheckboxGroup
        {...rest}
        name={name}
        {...getHandlers({...rest, ref})}
        value={useValue()}
        showingErrors={showErrors}
      />
    </NeoFieldWrapper>
  );
};

export const CheckboxGroupField = fixedForwardRef(InnerCheckboxGroupField);

type TagSelectFieldProps<T> = NeoFieldProps<T[], DSTagSelectProps<T>, T[]>;

const InnerTagSelectField = <const T,>(
  props: TagSelectFieldProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const {field, label, hint, wrapperClassName, ...rest} = props;
  const {useValue, getHandlers, id} = field;
  return (
    <NeoFieldWrapper field={field} label={label} hint={hint} className={wrapperClassName}>
      <DSTagSelect id={id} {...rest} {...getHandlers({...rest, ref})} value={useValue()} />
    </NeoFieldWrapper>
  );
};

export const TagSelectField = fixedForwardRef(InnerTagSelectField);

type CheckboxFieldProps = {
  field: NeoField<boolean, boolean>;
  label: string;
  hint?: ReactNode;
  wrapperClassName?: string;
  pending?: boolean;
  disabled?: boolean;
};

export const DSCheckboxField = forwardRef<HTMLButtonElement, CheckboxFieldProps>(
  ({field, label, hint, wrapperClassName, pending, disabled}, ref) => {
    const {useValue, useMeta, getHandlers, id, useCustomDataSlice} = field;

    // Error handling
    const showErrors = useMeta((meta) => shouldShowError(meta));
    const errors = useMeta((meta) =>
      shouldShowError(meta) ? meta.errors.map((e) => e.message) : []
    );
    const serverError = useCustomDataSlice("serverFieldError", (s) => s?.[field.name]);
    const allErrors = serverError ? [...errors, serverError] : errors;
    const firstError = allErrors[0];

    const value = useValue();
    const handlers = getHandlers({ref});

    return (
      <Col sp="8px" className={wrapperClassName}>
        <label className={css({sp: "8px", display: "flex", align: "start"})} htmlFor={id}>
          <DSCheckbox
            id={id}
            checked={value}
            hasError={showErrors}
            pending={pending}
            disabled={disabled}
            {...handlers}
          />
          <Text type="label14" color="primary" as="span">
            {label}
          </Text>
          {showErrors && firstError && (
            <Text type="label12" color="error" as="span">
              {" "}
              {firstError}
            </Text>
          )}
        </label>
        {hint && (
          <Text type="label12light" allowWrap color="secondary">
            {hint}
          </Text>
        )}
      </Col>
    );
  }
);
